import { useEffect, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineDownload, HiOutlinePrinter } from "react-icons/hi";
import { Button } from "../Button/Button";

interface InvoiceDocumentProps {
  filename: string;
  loadPreview?: () => Promise<string>;
  providerDocument?: {
    filename: string;
    label: string;
    download: () => Promise<Blob>;
  };
}

export const InvoiceDocument: FC<InvoiceDocumentProps> = ({
  filename,
  loadPreview,
  providerDocument,
}) => {
  const { t } = useTranslation(["common", "invoice"]);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [isLoading, setIsLoading] = useState(Boolean(loadPreview));
  const [hasError, setHasError] = useState(false);
  const [isDownloadingProviderDocument, setIsDownloadingProviderDocument] =
    useState(false);
  const [providerDocumentError, setProviderDocumentError] = useState(false);

  const fetchPreview = async () => {
    if (!loadPreview) return;
    setIsLoading(true);
    setHasError(false);
    try {
      const html = await loadPreview();
      if (!html?.trim()) throw new Error("Empty invoice preview");
      setPreviewHtml(html);
    } catch (error) {
      console.error("Error loading invoice document preview:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void fetchPreview();
    // The tab is remounted for each selected invoice, so loading once is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadProviderDocument = async () => {
    if (!providerDocument) return;
    setIsDownloadingProviderDocument(true);
    setProviderDocumentError(false);
    try {
      const document = await providerDocument.download();
      const url = window.URL.createObjectURL(document);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = providerDocument.filename;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading provider invoice document:", error);
      setProviderDocumentError(true);
    } finally {
      setIsDownloadingProviderDocument(false);
    }
  };

  return (
    <section className="py-4" aria-label={t("invoice:tabs.document")}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-sm text-muted">{filename}</p>
        <div className="flex gap-2">
          {providerDocument ? (
            <Button
              color="light"
              size="sm"
              onClick={() => void downloadProviderDocument()}
              disabled={isDownloadingProviderDocument}
            >
              <HiOutlineDownload className="mr-2 h-4 w-4" />
              {providerDocument.label}
            </Button>
          ) : null}
          <Button
            color="light"
            size="sm"
            onClick={() => iframeRef.current?.contentWindow?.print()}
            disabled={!previewHtml || isLoading}
          >
            <HiOutlinePrinter className="mr-2 h-4 w-4" />
            {t("invoice:actions.print")}
          </Button>
        </div>
      </div>
      {providerDocumentError ? (
        <p className="mb-3 text-sm text-danger">
          {t("invoice:accounting_integration.provider_document_error")}
        </p>
      ) : null}

      <div className="min-h-[28rem] overflow-hidden rounded-lg border border-border bg-surface-muted">
        {isLoading && (
          <div className="flex min-h-[28rem] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand" />
          </div>
        )}
        {!isLoading && hasError && (
          <div className="flex min-h-[28rem] flex-col items-center justify-center gap-3 px-4 text-center">
            <p className="text-sm text-muted">
              {t("invoice:actions.preview_error")}
            </p>
            <Button color="light" size="sm" onClick={() => void fetchPreview()}>
              {t("common:try_again", "Try again")}
            </Button>
          </div>
        )}
        {!isLoading && !hasError && previewHtml && (
          <iframe
            ref={iframeRef}
            srcDoc={previewHtml}
            className="h-[70vh] min-h-[38rem] w-full border-0 bg-white"
            title={t("invoice:actions.preview_dialog_title")}
            sandbox="allow-same-origin allow-modals"
          />
        )}
      </div>
    </section>
  );
};
